import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuthentication } from "meportal/AuthenticationProvider";
import './FleetDetails.scss';
import LoadingSpinner from '../../../helpers/loadingSpinner';
import { getOpenEvents, getLocation, getAircraftInFleet } from '../../../services/otsEventService';
import { formatDateTime } from '../../../helpers/DateTime';
import dayjs from "dayjs";
import { useEventType } from '../../../providers/EventTypeProvider';
import { useInlineNotification } from "../../../providers/InlineNotificationProvider";
import { INLINE_NOTIFICATION_TYPES } from '../../InlineNotification/inlineNotificationConstants';
import { AircraftData } from '../../../Models/CreateFleetOts';
import { OtsAircraft } from '../../../Models/OtsAircraft';

const formatStationTime = (inputTime: string): string => {
    const convertedTime = dayjs(inputTime);
    return convertedTime.format("HH:mm");
};

interface FleetDetailsProps {
    fleetTypeSelection: string;
    onDataUpdate: (data: AircraftData[][], openEvents: OtsAircraft[]) => void;
    onValidDataShowing: (isValid: boolean) => void;
    inServiceAircraftData: AircraftData[][];
}

const FleetDetails: React.FC<FleetDetailsProps> = ({ fleetTypeSelection, onDataUpdate, onValidDataShowing, inServiceAircraftData }) => {
    const { eventTypes, selectedEventType, isEventTypeHttpError } = useEventType();
    const { userData, getLatestAccessToken } = useAuthentication();
    const [airborneData, setAirborneData] = useState<AircraftData[]>([]);
    const [otsAircraftData, setOtsAircraftData] = useState<AircraftData[]>([]);
    const [outAircraftData, setOutAircraftData] = useState<AircraftData[]>([]);
    const [inAircraftData, setInAircraftData] = useState<AircraftData[]>([]);
    const [existingOpenEvents, setExistingOpenEvents] = useState<OtsAircraft[]>([]);
    const [airborneLoading, setAirborneLoading] = useState(true);
    const [otsAircraftLoading, setOtsAircraftLoading] = useState(true);
    const [outAircraftLoading, setOutAircraftLoading] = useState(true);
    const [inAircraftLoading, setInAircraftLoading] = useState(true);
    const [totalAircraftCount, setTotalAircraftCount] = useState(0);
    const [displayedAircraftCount, setDisplayedAircraftCount] = useState(0);
    const [retryCounter, setRetryCounter] = useState(0);
    const sectionTitle = 'Fleet Details';
    const airborneEvents = 'Airborne';
    const otsAircraftEvents = 'OTS Aircraft';
    const outAircraftEvents = 'OUT Aircraft';
    const inAircraftEvents = 'Gated IN';
    const aircraftHeader = 'Aircraft';
    const stationHeader = 'Station';
    const locationHeader = 'Loc';
    const etaHeader = 'ETA';
    const statusHeader = 'Status';
    const eventTypeHeader = 'Event Type';
    const outHeader = 'Out';
    const { addInlineNotification, removeInlineNotification } = useInlineNotification();
    const retryMaxReachedMessage = 'You have reached the max retry attempt.  Please exit this screen and start again or contact support if the issue persists.';
    const aircraftDetailsErrorTitle = 'Error occurred while fetching aircraft details';
    const tryAgainCountRef = useRef(retryCounter);
    const isMountedRef = useRef(false);
    const fleetSectionEmpty = 'No aircraft in this category';
    useEffect(() => {
        // Set the ref to true when the component mounts
        isMountedRef.current = true;

        // Cleanup function to set the ref to false when the component unmounts
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        tryAgainCountRef.current = retryCounter;
    }, [retryCounter]);

    const rowColor = (index: number) => {
        return index % 2 === 0 ? "fleet-row gray-row" : "fleet-row white-row";
    };

    const removeNPrefix = (registration: string) => registration.startsWith('N') ? registration.slice(1) : registration;

    const processOpenEvents = useCallback(async (openEventsData: OtsAircraft[], fleetTypeItems: any[]) => {
        setAirborneLoading(true);
        setOtsAircraftLoading(true);

        const filteredAircraft = openEventsData.filter((item: any) =>
            fleetTypeItems.some((fleetItem: any) => `N${item.AircraftRegistration}` === fleetItem.AircraftRegistration)
        );

        const newOtsAircraftData = filteredAircraft
            .map((item: any) => ({
                aircraft: item.AircraftRegistration,
                station: item.Station,
                eventType: item.EventType,
                status: item.status || '--',
                isEtopsCapable: item.IsEtopsCapable
            }));

        setOtsAircraftData(newOtsAircraftData);
        setExistingOpenEvents(filteredAircraft);
        setDisplayedAircraftCount(prevCount => prevCount + newOtsAircraftData.length);
        setAirborneLoading(false);
        setOtsAircraftLoading(false);
    }, [eventTypes]);

    const processBatchResults = useCallback((results: any[]) => {
        const newAirborneData = results
            .filter(({ result }) => result.IsAirborne)
            .map(({ result, item }) => ({
                aircraft: removeNPrefix(item.AircraftRegistration),
                station: result.Station,
                eta: formatDateTime(result?.ActiveFlight?.ScheduledEstimatedArrivalTime) || '--',
                isEtopsCapable: item.IsETOPSCapable,
                location: "",
                status: '--'
            }));

        const newOutAircraftData = results
            .filter(({ result }) => result?.fleetActiveFlight != null && !result.IsAirborne)
            .map(({ result, item }) => ({
                aircraft: removeNPrefix(item.AircraftRegistration),
                station: result?.fleetActiveFlight?.DepartureStation || "",
                outTime: formatStationTime(result?.fleetActuals?.Out) || '--',
                isEtopsCapable: item.IsETOPSCapable,
                location: result?.Location || "",
                status: '--'
            }));

        const newInAircraftData = results
            .filter(({ result }) => result?.fleetLastCompletedFlight != null)
            .map(({ result, item }) => ({
                aircraft: removeNPrefix(item.AircraftRegistration),
                station: result?.fleetLastCompletedFlight?.ArrivalStation || "",
                location: result?.Location || "",
                isEtopsCapable: item.IsETOPSCapable,
                status: '--'
            }));

        setAirborneData(prevData => [...prevData, ...newAirborneData]);
        setOutAircraftData(prevData => [...prevData, ...newOutAircraftData]);
        setInAircraftData(prevData => [...prevData, ...newInAircraftData]);
        setDisplayedAircraftCount(prevCount => prevCount + newAirborneData.length + newOutAircraftData.length + newInAircraftData.length);
    }, []);

    const processRemainingAircraft = useCallback(async (fleetTypeItems: any[], openEventsData: any[]) => {
        setAirborneLoading(true);
        setOutAircraftLoading(true);
        setInAircraftLoading(true);

        const remainingAircraft = fleetTypeItems.filter((fleetItem: any) =>
            !openEventsData.some((openEventItem: any) => `N${openEventItem.AircraftRegistration}` === fleetItem.AircraftRegistration)
        );

        const BATCH_SIZE = 5;
        for (let i = 0; i < remainingAircraft.length; i += BATCH_SIZE) {
            const batch = remainingAircraft.slice(i, i + BATCH_SIZE);
            const batchPromises = batch.map((item: any) =>
                getLocation(userData, item.AircraftRegistration).then(result => ({ result, item }))
            );
            const batchResults = await Promise.all(batchPromises);
            processBatchResults(batchResults);
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        setAirborneLoading(false);
        setOutAircraftLoading(false);
        setInAircraftLoading(false);
    }, [userData, processBatchResults]);
 
    useEffect(() => {
      const isValidData = displayedAircraftCount > 0 && displayedAircraftCount === totalAircraftCount;
      onValidDataShowing(isValidData);
    }, [displayedAircraftCount, totalAircraftCount, onValidDataShowing]);

    const fetchData = useCallback(async () => {
            setAirborneLoading(true);
            setOtsAircraftLoading(true);
            setOutAircraftLoading(true);
            setInAircraftLoading(true);
            setAirborneData([]);
            setOtsAircraftData([]);
            setOutAircraftData([]);
            setInAircraftData([]);
            setDisplayedAircraftCount(0);
            removeInlineNotification();
            try {
                const token = await getLatestAccessToken();
                userData.access_token = token;
                const aircraftInFleet = await getAircraftInFleet(userData, fleetTypeSelection);
                
                if (isMountedRef.current) {
                    if (aircraftInFleet === null) {
                        setTotalAircraftCount(0);
                        setAirborneLoading(false);
                        setOtsAircraftLoading(false);
                        setOutAircraftLoading(false);
                        setInAircraftLoading(false);
                        return;
                    }
                    setTotalAircraftCount(aircraftInFleet.length);
                }
                if (aircraftInFleet.length > 0) {
                    const openEventsData = await getOpenEvents(userData, "");
                    if (isMountedRef.current) {
                        await processOpenEvents(openEventsData, aircraftInFleet);
                        await processRemainingAircraft(aircraftInFleet, openEventsData);
                    }
                }
            } catch (error) {
                if (isMountedRef.current) {
                    setAirborneLoading(false);
                    setOutAircraftLoading(false);
                    setInAircraftLoading(false);
                    setOtsAircraftLoading(false);
                    setRetryCounter(prev => prev + 1);

                    addInlineNotification(INLINE_NOTIFICATION_TYPES.WARNING, aircraftDetailsErrorTitle, 
                        '', retryMaxReachedMessage, () => fetchData(), tryAgainCountRef.current );
                }
            }
        
    }, [fleetTypeSelection, getLatestAccessToken, processOpenEvents, processRemainingAircraft, userData]);
 
    useEffect(() => {
        if(Array.isArray(inServiceAircraftData) && inServiceAircraftData.length >= 4){
            if(inServiceAircraftData[0].length > 0)
                setOutAircraftData(inServiceAircraftData[0]);
            if(inServiceAircraftData[1].length > 0)
                setAirborneData(inServiceAircraftData[1]);
            if(inServiceAircraftData[2].length > 0)
                setInAircraftData(inServiceAircraftData[2]);
            if(inServiceAircraftData[3].length > 0)
                setOtsAircraftData(inServiceAircraftData[3]);
        }
    }, [inServiceAircraftData]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);


    useEffect(() => {
        if (totalAircraftCount === displayedAircraftCount) {
            const airborneEvent = Array.isArray(eventTypes) ? eventTypes.find(eventType => eventType.Category == "NotApplicable" && eventType.IsForAirborne && eventType.IsActive) : null;
            // Add airborneEvent property to each object in airborneData
            const updatedAirborneData = airborneData.map(data => ({
                ...data,
                airborneEvent: airborneEvent
            }));
            const combinedAircraftData = [outAircraftData, updatedAirborneData, inAircraftData, otsAircraftData];
            onDataUpdate(combinedAircraftData, existingOpenEvents);
        }
    }, [totalAircraftCount, displayedAircraftCount, eventTypes]);
    const formatTime = (inputTime: string, isTime: boolean): string => {
        const convertedTime = dayjs.utc(inputTime).local();
        return isTime ? convertedTime.format("HH:mm") : convertedTime.format("MM/DD/YY");
    };

    const renderTable = (data: AircraftData[], headers: string[], title: string, isLoading: boolean) => (
        <>
            <h3 className="fleet-table event-titles">
                {title} ({data.length})
                {isLoading && <LoadingSpinner isLoading={true} />}
            </h3>
    {data.length === 0 && !isLoading ? (
      <p className="fleet-empty">{fleetSectionEmpty}</p>
    ) : (<table className='fleet-type-table'>
                <thead>
                    <tr>
                        {!isLoading ? headers.map((header, index) => (
                            <th key={index} className="fleet-headers">{header}</th>
                        )): ''}
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, index) => (
                        <tr key={index} className={rowColor(index)}>
                            <td>{item.aircraft}</td>
                            <td>{item.station ? item.station : '--'}</td>
                            {item.outTime && headers.includes(outHeader) && <td>{item.outTime}</td>}
                            {headers.includes(locationHeader) && <td>{item.location ? item.location : '--'}</td>}
                            {item.eta && headers.includes(etaHeader) && <td>{item.eta}</td>}
                            {item.eventType && headers.includes(eventTypeHeader) && <td>{item.eventType}</td>}
                            <td>{item.status}</td>
                        </tr>
                    ))}
                </tbody>
            </table>)}
        </>
    );

    return (
        <div className='fleet-details'>
            <h2 className='section-title'>
                {sectionTitle} ({displayedAircraftCount}/{totalAircraftCount})
            </h2>
            {renderTable(outAircraftData, [aircraftHeader, stationHeader, outHeader, statusHeader], outAircraftEvents, outAircraftLoading)}
            {renderTable(airborneData, [aircraftHeader, stationHeader, etaHeader, statusHeader], airborneEvents, airborneLoading)}
            {renderTable(otsAircraftData, [aircraftHeader, stationHeader, eventTypeHeader, statusHeader], otsAircraftEvents, otsAircraftLoading)}
            {renderTable(inAircraftData, [aircraftHeader, stationHeader, locationHeader, statusHeader], inAircraftEvents, inAircraftLoading)}
        </div>
    );
};

export default FleetDetails;